<template>
    <Teleport to="body">

        <div class="quick-feedback-wrapper" v-if="isSurveyOpen && isFeedbackError">
            <Card class="feedback-card thank-you-wrapper">
                <template #content>
                    <div class="p-text-center p-mb-4 ">
                        <p class="p-text-semibold p-mb-3 text-red-500">{{ errorMsg }}</p>
                    </div>
                </template>
            </Card>
        </div>

        <div class="quick-feedback-wrapper" v-else-if="isSurveyOpen && displaySurveyToUser && !isFeedbackSubmitted">
            <Card class="feedback-card">
                <template #title>
                    <div class="feedback-header">
                        <span>We value your feedback!</span>
                        <Button icon="pi pi-times" class="p-button-text p-button-secondary p-button-sm p-ml-auto"
                            @click="close(false)" aria-label="Close Feedback" />
                    </div>
                </template>

                <template #content>
                    <div v-if="isLoading" class="text-center">
                        <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" />
                    </div>
                    <div v-else>
                        <div class="p-text-center p-mb-4 rating-wrapper">
                            <p class="p-text-semibold p-mb-3">How satisfied are you with PoshPop?</p>

                            <Rating v-model="rating" :stars="5" :cancel="false" class="feedback-rating"
                                aria-label="Star Rating" />
                        </div>

                        <div class="p-fluid feedback-wrapper">
                            <label for="comment" class="p-sr-only">Feedback Comment</label>
                            <Textarea id="comment" v-model="comment" rows="3" :autoResize="true"
                                :placeholder="commentPlaceholder" :maxlength="maxCommentLength" />

                            <div class="p-text-right p-text-sm p-mt-1 char-count-wrapper">
                                {{ comment.length }}/{{ maxCommentLength }}
                            </div>
                        </div>
                    </div>
                </template>

                <template #footer>
                    <div class="p-d-flex p-jc-end submit-feedback-btn-wrapper">
                        <Button label="Submit Feedback" :disabled="rating === 0 || isLoading" @click="submitFeedback"
                            class="p-button-success" />
                    </div>
                </template>

            </Card>
        </div>

        <div class="quick-feedback-wrapper" v-else-if="isSurveyOpen && isFeedbackSubmitted">
            <Card class="feedback-card thank-you-wrapper">
                <template #content>
                    <div class="p-text-center p-mb-4 ">
                        <h3 class="p-text-semibold p-mb-3">Thank you for your feedback!</h3>
                    </div>
                </template>
            </Card>
        </div>

    </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import Card from 'primevue/card';
import Rating from 'primevue/rating';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import { currentUserVersion, serverUrl } from '@/globals/env';
import { useAuthStore } from '../stores/auth';
import { logError, sendMessage, sleep } from '@/utils';
import LocalStorageWrapper from '@/classes/LocalStorageWrapper'
import { differenceInDays, parseISO } from 'date-fns';
import { usePoshmarkUser } from '@/stores/PoshmarkUserStore';
import ProgressSpinner from 'primevue/progressspinner'

const maxCommentLength = 200;
const snoozeDurationInDays = 30
const MINIMUM_DAYS_FOR_SURVEY = 6
const localStorageWrapper = LocalStorageWrapper.getInstance()
const authStore = useAuthStore()
const rating = ref(0);
const comment = ref('');
const isLoading = ref(false);
const isFeedbackSubmitted = ref(false);
const isFeedbackError = ref(false);
const lastSnoozedDate = ref<number | null>(null)
const isSurveyOpen = ref(false);
const poshmarkUser = usePoshmarkUser()
const errorMsg = ref('')
const poshmarkLocation = ref(null)

const displaySurveyToUser = computed(() => {
    const userCreationDate = authStore.user?.user?.created_at ? parseISO(authStore.user?.user?.created_at) : null
    const daysOld = userCreationDate ? differenceInDays(new Date(), userCreationDate) : 0

    return authStore.loggedIn && authStore.user && authStore.user.user && authStore.user.user.feedback_submitted_at === null && daysOld >= MINIMUM_DAYS_FOR_SURVEY
})

const commentPlaceholder = computed(() => {
    if (rating.value >= 4) {
        return 'What did you like the most? (Optional, but helpful)';
    }
    return 'How can we improve? (Optional, but helpful)';
});

const close = (hasFailed = false) => {
    if (!hasFailed)
        localStorageWrapper.set('snooze-survey-date', Date.now().toString())
    isSurveyOpen.value = false;
};

onMounted(async () => {
    const storedDate = await localStorageWrapper.get('snooze-survey-date');
    lastSnoozedDate.value = storedDate ? Number(storedDate) : 0;
    const daysPassed = differenceInDays(Date.now(), lastSnoozedDate.value)
    window.addEventListener('message', feedbackMessagesHandler)

    if (!storedDate || daysPassed >= snoozeDurationInDays) {
        isSurveyOpen.value = true
    } else {
        isSurveyOpen.value = false
    }
})

const sendFeedbackApi = async () => {
    if (isLoading.value) return;

    isLoading.value = true;

    const data = {
        rating: rating.value,
        comment: comment.value.trim().substring(0, maxCommentLength),
        store: poshmarkUser.username,
        version: currentUserVersion,
        country: poshmarkLocation.value ?? null
    };


    const maxRetries = 3
    const initialDelay = 2000

    for (let i = 0; i < maxRetries; i++) {
        try {
            const options = {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'content-type': 'application/json',
                    Authorization: 'Bearer ' + authStore.user.token
                },
                body: JSON.stringify(data)
            }
            const response = await fetch(serverUrl + 'feedback', options)

            if (!response.ok) {
                throw new Error(`Failed to submit feedback: ${response.status} - ${response.statusText}`)
            }

            // Show thank you message for 3 seconds, then close
            isFeedbackSubmitted.value = true;
            setTimeout(close, 3000);

            break;
        } catch (error) {
            if (i + 1 >= maxRetries) {
                logError("Feedback submission error:", error);
                errorMsg.value = "Oops! Looks like we encountered an a problem receiving your feedback."
                isFeedbackError.value = true;
                setTimeout(() => close(true), 3000);
            }

            await sleep(initialDelay)
        }
    }

    isLoading.value = false;
};

const submitFeedback = () => {
    if (rating.value === 0) return;
    sendFeedbackApi();
};

const feedbackMessagesHandler = async (event: any) => {
    if (event.data.message == 'set-poshmark-location') {
        poshmarkLocation.value = event.data.country;
        window.removeEventListener('message', feedbackMessagesHandler)
    }
}
</script>

<style lang="less">
/* Add style for the character count wrapper */
.char-count-wrapper {
    color: var(--text-color-secondary, #6c757d);
    font-size: 0.75rem;
    text-align: right;
}

/* --- Core Wrapper and Card Styling --- */
.quick-feedback-wrapper {
    position: fixed;
    bottom: 20px;
    right: 30px;
    z-index: 99999;
    border: 2px solid #0070b7;
    border-radius: 5px;
}

.quick-feedback-submitted {
    position: fixed;
    top: 50%;
    left: 30px;
    z-index: 99999;
    border: 2px solid #0070b7;
    border-radius: 5px;
}

.thank-you-wrapper {
    text-align: center;

    .p-card-content {
        padding: 0;
    }

    h3 {
        font-size: 20px;
        font-weight: bold;
    }
}

.thank-you-header {
    display: flex;
    justify-content: end;
}

.feedback-card {
    width: 400px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.feedback-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 1.1rem;
    font-weight: 600;
}

.feedback-rating {
    display: flex;
    margin: 0 auto;
}

.rating-wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 10px;

    p {
        font-size: 18px;
    }
}

.feedback-wrapper {
    margin-top: 15px;
}

.p-card-footer {
    padding-top: 0;
    text-align: center;
}

#comment {
    height: 96px;
    overflow: hidden;
    border: 2px solid rgb(228, 228, 228);
    resize: vertical;
    min-height: 100px;
    padding: 5px;
}

.p-rating-icon {
    width: 30px;
    margin-right: 0.5rem;
    height: 30px;
}

.submit-feedback-btn-wrapper {
    background-color: #0070b7;
    color: white;
    border-radius: 5px;
    border-color: #0070b7;

    button {
        width: 100%;
        padding: 5px 20px;
    }
}
</style>